import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import Spinner from '../../../components/ui/Spinner';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../components/icons';
import DashboardCard from '../../dashboard/components/DashboardCard';
import { useAdminInvoices } from '../hooks/useAdminInvoices';
import { InvoicesFilters } from '../invoices/InvoicesFilters';
import { InvoicesTable } from '../invoices/InvoicesTable';

const DEBOUNCE_MS = 300;

const navBtn =
  'flex h-8 w-8 items-center justify-center rounded-md border border-subtle bg-card text-secondary transition-colors hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)] disabled:cursor-not-allowed disabled:opacity-40';

export function InvoicesSection() {
  const {
    data,
    isLoading,
    error,
    refetch,
    page,
    setPage,
    setQ,
    filters,
    patchFilters,
    pageSize,
  } = useAdminInvoices();
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(searchInput);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, setQ, setPage]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <section aria-label="Histórico de faturas" className="mb-s5">
      <DashboardCard title="Histórico geral de faturas">
        <p className="mb-s4 text-[12px] text-muted">
          Faturas de todas as organizações · filtros por status e busca
        </p>

        <InvoicesFilters
          filters={filters}
          onChange={patchFilters}
          search={searchInput}
          onSearchChange={setSearchInput}
        />

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Spinner color="primary" size="lg" />
          </div>
        )}

        {!isLoading && error && (
          <Alert variant="destructive">
            <AlertTitle>Erro ao carregar faturas</AlertTitle>
            <AlertDescription className="flex flex-col gap-3">
              <p>{error}</p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Tentar novamente
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !error && data && (
          <>
            <div className="min-w-0">
              <InvoicesTable items={data.items} />
            </div>
            {total > 0 && (
              <div className="mt-s3 flex flex-col gap-3 border-t border-subtle pt-s3 text-sm text-secondary sm:flex-row sm:items-center sm:justify-between">
                <span>
                  {rangeStart}–{rangeEnd} de {total}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs">
                    Página {page} de {totalPages}
                  </span>
                  <button
                    type="button"
                    className={navBtn}
                    onClick={() => setPage(page - 1)}
                    disabled={!canPrev}
                    aria-label="Página anterior"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className={navBtn}
                    onClick={() => setPage(page + 1)}
                    disabled={!canNext}
                    aria-label="Próxima página"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </DashboardCard>
    </section>
  );
}
